import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { showError, showSuccess } from "../../services/toaster";
import { uploadFile } from "../../utils/helper";
import { payoutService } from "./service";
import { inputDateToUnix, monthLabel } from "./constant";
import { PayoutBilling } from "../../types/payouts";

type BillingTabProps = {
  specialistId: string;
  onDisbursalCreated: () => void;
};

const BillingTab: React.FC<BillingTabProps> = ({
  specialistId,
  onDisbursalCreated,
}) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = React.useState(currentYear);
  const [billings, setBillings] = React.useState<PayoutBilling[]>([]);
  const [selectedBillingIds, setSelectedBillingIds] = React.useState<string[]>([]);
  const [disbursalAmount, setDisbursalAmount] = React.useState("");
  const [disbursalDate, setDisbursalDate] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [receipts, setReceipts] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);

  const years = React.useMemo(
    () => Array.from({ length: 6 }, (_, index) => currentYear - index),
    [currentYear]
  );

  const fetchBillings = React.useCallback(async () => {
    if (!specialistId) return;
    setLoading(true);
    try {
      const data = await payoutService.fetchBillings(specialistId, selectedYear);
      setBillings(Array.isArray(data) ? (data as PayoutBilling[]) : []);
      setSelectedBillingIds([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, specialistId]);

  React.useEffect(() => {
    fetchBillings();
  }, [fetchBillings]);

  const selectedBillings = billings.filter((billing) =>
    selectedBillingIds.includes(billing._id)
  );
  const totalDueSelected = selectedBillings.reduce(
    (sum, billing) => sum + (Number(billing.due) || 0),
    0
  );

  const handleCreateYear = async () => {
    setLoading(true);
    try {
      await payoutService.calculateYear(specialistId, selectedYear);
      showSuccess(`Billing generated for ${selectedYear}`);
      await fetchBillings();
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (month: number) => {
    const data = await payoutService.getInvoice(specialistId, month);
    if (data?.invoice_url) {
      window.open(`http://localhost:4030${data.invoice_url}`, "_blank");
    }
  };

  const handleCreateDisbursal = async () => {
    if (!selectedBillingIds.length) {
      showError("Select at least one billing");
      return;
    }
    if (!disbursalAmount || Number(disbursalAmount) <= 0) {
      showError("Enter a valid disbursal amount");
      return;
    }
    if (Number(disbursalAmount) > totalDueSelected) {
      showError("Disbursal amount cannot exceed total due");
      return;
    }
    if (!disbursalDate) {
      showError("Disbursal date is required");
      return;
    }

    setLoading(true);
    try {
      const created = await payoutService.createDisbursal({
        specialist_id: specialistId,
        billing_ids: selectedBillingIds,
        disbursal_amount: Number(disbursalAmount),
        disbursal_date: inputDateToUnix(disbursalDate),
        extra_details: {
          notes,
        },
      });

      if (created?._id && receipts.length) {
        const uploadRes = await uploadFile(
          { module: "payouts", recordId: created._id },
          receipts
        );
        const urls =
          uploadRes?.data?.data?.files?.map((file: { url: string }) => file.url) || [];
        if (urls.length) {
          await payoutService.updateDisbursal({
            _id: created._id,
            extra_details: {
              notes,
              receipts: urls,
            },
          });
        }
      }

      showSuccess("Disbursal created successfully");
      setDisbursalAmount("");
      setDisbursalDate("");
      setNotes("");
      setReceipts([]);
      await fetchBillings();
      onDisbursalCreated();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <TextField
          select
          label="Select Year"
          size="small"
          value={selectedYear}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSelectedYear(Number(e.target.value))
          }
          SelectProps={{ native: true }}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleCreateYear} disabled={loading}>
          Create Billing for {selectedYear}
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Select</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Appointments</TableCell>
              <TableCell>View Invoice</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billings.map((billing: PayoutBilling) => (
              <TableRow key={billing._id}>
                <TableCell>
                  <Checkbox
                    checked={selectedBillingIds.includes(billing._id)}
                    disabled={loading || Number(billing.due) === 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSelectedBillingIds((prev) =>
                        e.target.checked
                          ? [...prev, billing._id]
                          : prev.filter((id) => id !== billing._id)
                      );
                    }}
                  />
                </TableCell>
                <TableCell>{monthLabel(billing.month)}</TableCell>
                <TableCell>{billing.total}</TableCell>
                <TableCell>{billing.paid}</TableCell>
                <TableCell>{billing.due}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={billing.status}
                    color={
                      billing.status === "Paid"
                        ? "success"
                        : billing.status === "Partially Paid"
                          ? "warning"
                          : "default"
                    }
                  />
                </TableCell>
                <TableCell>{billing.totalAppointments}</TableCell>
                <TableCell>
                  <Button variant="text" onClick={() => handleViewInvoice(billing.month)}>
                    View Invoice
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!billings.length ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2">No billings found.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Create Disbursal</Typography>
          <Typography variant="body2">Total Due: {totalDueSelected}</Typography>
          <TextField
            label="Disbursal Amount"
            type="number"
            value={disbursalAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDisbursalAmount(e.target.value)
            }
          />
          <TextField
            label="Disbursal Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={disbursalDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDisbursalDate(e.target.value)
            }
          />
          <TextField
            label="Notes"
            multiline
            minRows={3}
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNotes(e.target.value)
            }
          />
          <Box>
            <Button variant="outlined" component="label">
              Upload Receipts
              <input
                hidden
                multiple
                type="file"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setReceipts(Array.from(e.target.files || []))
                }
              />
            </Button>
            {receipts.length ? (
              <Typography variant="body2" mt={1}>
                {receipts.length} receipt file(s) selected
              </Typography>
            ) : null}
          </Box>
          <Box>
            <Button variant="contained" onClick={handleCreateDisbursal} disabled={loading}>
              Create Disbursal
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default BillingTab;
